export default function logClientError(errorData) {
  const userId = window.currentUserId || null; // Injected from Laravel Blade

  // Categorize errors for better debugging
  const getErrorCategory = (data) => {
    const message = (data.message || '').toLowerCase();

    // Network/API errors
    if (
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('http') ||
      message.includes('timeout') ||
      message.includes('cors') ||
      message.includes('axios')
    ) {
      return 'network';
    }

    // JavaScript syntax/runtime errors
    if (
      message.includes('syntax') ||
      message.includes('reference') ||
      message.includes('type') ||
      message.includes('cannot read') ||
      message.includes('is not defined')
    ) {
      return 'javascript';
    }

    // React/Component errors
    if (
      message.includes('react') ||
      message.includes('component') ||
      message.includes('hook') ||
      data.componentStack
    ) {
      return 'react';
    }

    // Promise/Async errors
    if (
      data.type === 'unhandledrejection' ||
      message.includes('promise') ||
      message.includes('async') ||
      message.includes('await')
    ) {
      return 'async';
    }

    // DOM errors
    if (
      message.includes('dom') ||
      message.includes('element') ||
      message.includes('queryselector')
    ) {
      return 'dom';
    }

    return 'other';
  };

  // Normalize and validate error data
  const normalizeErrorData = (data) => {
    // Handle different error types and ensure consistent structure
    let normalized = { ...data };

    // Ensure errorName is always present and meaningful
    if (!normalized.errorName || normalized.errorName === 'UnknownError') {
      // Try to extract a meaningful name from the error object
      if (data.error && typeof data.error === 'object') {
        normalized.errorName = data.error.constructor?.name || data.error.name || 'UnknownError';
      } else if (data.reason && typeof data.reason === 'object') {
        normalized.errorName = data.reason.constructor?.name || data.reason.name || 'UnknownError';
      } else if (data.type === 'unhandledrejection') {
        normalized.errorName = 'UnhandledPromiseRejection';
      } else if (data.type === 'error') {
        normalized.errorName = 'GlobalError';
      } else {
        normalized.errorName = 'UnknownError';
      }
    }

    // Ensure message is always present
    if (!normalized.message) {
      if (data.error && data.error.message) {
        normalized.message = data.error.message;
      } else if (data.reason && data.reason.message) {
        normalized.message = data.reason.message;
      } else if (typeof data.reason === 'string') {
        normalized.message = data.reason;
      } else {
        normalized.message = 'No error message available';
      }
    }

    // Normalize stack trace
    if (!normalized.stack) {
      // Check multiple possible stack trace property names
      const possibleStackProps = ['stack', 'stackTrace', 'trace', 'backtrace', 'callStack'];

      for (const prop of possibleStackProps) {
        if (data.error && data.error[prop]) {
          normalized.stack = data.error[prop];
          break;
        } else if (data.reason && data.reason[prop]) {
          normalized.stack = data.reason[prop];
          break;
        }
      }
    }

    // Add error category for better debugging
    normalized.errorCategory = getErrorCategory(normalized);

    return normalized;
  };

  // Truncate stack traces to 200 characters
  const truncateStack = (stack) => {
    if (!stack) return null;

    try {
      return stack.length > 200 ? stack.substring(0, 200) + '...' : stack;
    } catch (error) {
      return stack; // Return original stack as fallback
    }
  };

  // Normalize the error data first
  const normalizedData = normalizeErrorData(errorData);

  // Ensure we capture stack traces
  const enhancedErrorData = {
    ...normalizedData,
    userId,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    referrer: document.referrer,
    stack: truncateStack(normalizedData.stack),
    componentStack: truncateStack(normalizedData.componentStack),
  };

  axios.post('/log-client-error', enhancedErrorData).catch((error) => {
    console.error('Error logging client error:', error);
  });
}
